// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { SnackMessage, httpStatus } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
  CertificationsPayload,
  GetCertificationsRequestState,
} from "../../utils/types";

import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetCertificationsRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: { awardededCertifications: [], expiredCertifications: [] },
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getCertifications = createAsyncThunk(
  "exams/getCertifications",
  async (certificationId: string, { dispatch }) => {
    return new Promise<{ certifications: CertificationsPayload[] }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(
            AppConfig.serviceUrls.getAllCertifications +
              "/?userCertificationId=" +
              certificationId
          )
          .then((resp) => {
            if (resp.status == 200) {
              resolve({ certifications: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.certificationHistory,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

export const CertificationHistorySlice = createSlice({
  name: "certification",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCertifications.pending, (state, action) => {
        state.stateMessage = "Loading Certifications";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Fetching Certifications Data";
      })
      .addCase(getCertifications.fulfilled, (state, action) => {
        state.response = {
          awardededCertifications: action.payload.certifications
          .filter((cert) => cert.isAwarded)
          .sort((a,b) => new Date(b.awardedDate).getTime() - new Date(a.awardedDate).getTime()),
          expiredCertifications: action.payload.certifications
          .filter(cert => !cert.isAwarded && cert.certificationExpirationDate)
          .sort((a, b) => new Date(b.certificationExpirationDate as string).getTime() - new Date(a.certificationExpirationDate as string).getTime()),
        };
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(getCertifications.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export default CertificationHistorySlice.reducer;
